import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Configuración de zonas horarias de Chile
 * Preparado para expandir a otros países de Sudamérica
 */
const CHILE_TIMEZONES = [
  {
    value: "America/Santiago",
    label: "Santiago (Chile Continental)",
    country: "Chile",
  },
  {
    value: "Pacific/Easter",
    label: "Isla de Pascua (Easter Island)",
    country: "Chile",
  },
];

// Preparado para agregar otros países
const OTHER_SOUTH_AMERICAN_TIMEZONES = [
  // Argentina: { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires', country: 'Argentina' },
  // Peru: { value: 'America/Lima', label: 'Lima', country: 'Perú' },
  // Colombia: { value: 'America/Bogota', label: 'Bogotá', country: 'Colombia' },
  // Brasil: { value: 'America/Sao_Paulo', label: 'São Paulo', country: 'Brasil' },
  // Ecuador: { value: 'America/Guayaquil', label: 'Guayaquil', country: 'Ecuador' },
  // Bolivia: { value: 'America/La_Paz', label: 'La Paz', country: 'Bolivia' },
  // Uruguay: { value: 'America/Montevideo', label: 'Montevideo', country: 'Uruguay' },
  // Paraguay: { value: 'America/Asuncion', label: 'Asunción', country: 'Paraguay' },
  // Venezuela: { value: 'America/Caracas', label: 'Caracas', country: 'Venezuela' },
];

const ALL_TIMEZONES = [...CHILE_TIMEZONES, ...OTHER_SOUTH_AMERICAN_TIMEZONES];

/**
 * Componente selector de zona horaria con preview de hora local
 * @param {string} value - Valor actual de la zona horaria (IANA timezone string)
 * @param {function} onChange - Callback cuando cambia la zona horaria
 * @param {string} previewTime - Hora a mostrar en el preview (formato HH:mm o ISO string)
 * @param {string} previewDate - Fecha a mostrar en el preview (formato yyyy-MM-dd o ISO string)
 */
export default function TimezoneSelector({
  value,
  onChange,
  previewTime = null,
  previewDate = null,
}) {
  const [gmtOffset, setGmtOffset] = useState("");
  const [userTimezone, setUserTimezone] = useState("");
  const [previewEventTime, setPreviewEventTime] = useState("");
  const [previewUserTime, setPreviewUserTime] = useState("");

  // Detectar zona horaria del usuario
  useEffect(() => {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(detectedTimezone);
    } catch (error) {
      console.error("Error detectando zona horaria del usuario:", error);
      setUserTimezone("UTC");
    }
  }, []);

  // Calcular GMT offset de la zona horaria seleccionada
  useEffect(() => {
    if (!value) return;

    try {
      const now = DateTime.now().setZone(value);
      const offset = now.toFormat("ZZZZ"); // Formato: GMT-3, GMT-4, etc.
      setGmtOffset(offset);
    } catch (error) {
      console.error("Error calculando GMT offset:", error);
      setGmtOffset("");
    }
  }, [value]);

  // Calcular preview de tiempo en ambas zonas horarias
  useEffect(() => {
    if (!value || !previewTime || !previewDate) {
      setPreviewEventTime("");
      setPreviewUserTime("");
      return;
    }

    try {
      // Crear DateTime en la zona horaria del evento
      const eventDateTime = DateTime.fromISO(
        `${previewDate}T${previewTime}:00`,
        { zone: value }
      );

      if (!eventDateTime.isValid) {
        console.error("DateTime inválido:", eventDateTime.invalidReason);
        setPreviewEventTime("");
        setPreviewUserTime("");
        return;
      }

      // Formatear hora en zona horaria del evento
      const eventTimeStr = eventDateTime.toFormat("HH:mm");
      setPreviewEventTime(eventTimeStr);

      // Convertir a zona horaria del usuario solo si es diferente
      if (userTimezone && userTimezone !== value) {
        const userDateTime = eventDateTime.setZone(userTimezone);
        const userTimeStr = userDateTime.toFormat("HH:mm");
        const userDateStr = userDateTime.toFormat("dd/MM");
        
        // Verificar si la fecha cambió al convertir
        const dateChanged = eventDateTime.toFormat("dd/MM") !== userDateStr;
        
        if (dateChanged) {
          setPreviewUserTime(`${userTimeStr} (${userDateStr})`);
        } else {
          setPreviewUserTime(userTimeStr);
        }
      } else {
        setPreviewUserTime("");
      }
    } catch (error) {
      console.error("Error calculando preview de tiempo:", error);
      setPreviewEventTime("");
      setPreviewUserTime("");
    }
  }, [value, previewTime, previewDate, userTimezone]);

  // Detectar automáticamente la zona horaria del navegador
  const handleAutoDetect = () => {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Verificar si la zona horaria detectada está en nuestra lista
      const isInList = ALL_TIMEZONES.some((tz) => tz.value === detectedTimezone);
      
      if (isInList) {
        onChange(detectedTimezone);
      } else {
        // Si no está en la lista, buscar zona similar o usar default
        // Para Chile, si detecta cualquier timezone de las Américas con UTC-3/UTC-4, usar Santiago
        const now = DateTime.now().setZone(detectedTimezone);
        const offsetMinutes = now.offset;
        
        // Chile Continental: UTC-3 (verano) o UTC-4 (invierno) = -180 o -240 minutos
        if (offsetMinutes === -180 || offsetMinutes === -240) {
          onChange("America/Santiago");
          alert(`Zona horaria detectada: ${detectedTimezone}.\nSe seleccionó "Santiago" como la más cercana.`);
        } else {
          onChange("America/Santiago"); // Default
          alert(`Zona horaria detectada: ${detectedTimezone}.\nNo está en la lista. Se seleccionó "Santiago" por defecto.`);
        }
      }
    } catch (error) {
      console.error("Error al detectar zona horaria:", error);
      onChange("America/Santiago");
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        <FontAwesomeIcon icon="fa-solid fa-globe" className="mr-2" />
        Zona Horaria del Evento
      </label>

      <div className="flex gap-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-sm text-white focus:outline-none focus:border-aw-green transition duration-300"
        >
          {ALL_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label} {gmtOffset && value === tz.value ? `(${gmtOffset})` : ""}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleAutoDetect}
          className="px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-sm text-gray-300 hover:bg-gray-700 hover:border-aw-green transition duration-300"
          title="Detectar automáticamente mi zona horaria"
        >
          <FontAwesomeIcon icon="fa-solid fa-location-crosshairs" />
        </button>
      </div>

      <div className="mt-2 space-y-1">
        <p className="text-xs text-gray-400">
          La fecha y hora se guardarán en esta zona horaria {gmtOffset && `(${gmtOffset})`}
        </p>

        {/* Preview de tiempo */}
        {previewEventTime && (
          <div className="mt-2 p-2 bg-aw-bluedark/50 border border-gray-700 rounded-sm">
            <p className="text-xs text-gray-300">
              <FontAwesomeIcon icon="fa-solid fa-clock" className="mr-1" />
              <span className="font-semibold">Hora del evento:</span> {previewEventTime} {gmtOffset && `(${gmtOffset})`}
            </p>
            {previewUserTime && userTimezone !== value && (
              <p className="text-xs text-gray-400 mt-1">
                <FontAwesomeIcon icon="fa-solid fa-user-clock" className="mr-1" />
                <span className="font-semibold">Tu hora local:</span> {previewUserTime}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
